/**
 * ExamExpirationBadge Component
 * Badge/Chip for displaying exam expiration status
 *
 * @version 2025.1.0
 */

import React from 'react';
import { Chip, ChipProps, Tooltip } from '@mui/material';
import {
  CheckCircle as OkIcon,
  Warning as WarningIcon,
  Error as ExpiredIcon,
} from '@mui/icons-material';
import {
  ExpirationStatus,
  ExpirationStatusLabels,
  ExpirationStatusColors,
  formatDaysUntilExpiration,
} from '../../types/examApplication';

// ============================================================================
// TYPES
// ============================================================================

export interface ExamExpirationBadgeProps extends Omit<ChipProps, 'label' | 'color' | 'icon'> {
  /** Expiration status */
  status: ExpirationStatus;
  /** Days until expiration (negative if expired) */
  daysUntilExpiration?: number | null;
  /** Show days in label */
  showDays?: boolean;
  /** Show icon */
  showIcon?: boolean;
  /** Show tooltip with details */
  showTooltip?: boolean;
}

// ============================================================================
// ICONS
// ============================================================================

const statusIcons: Record<ExpirationStatus, React.ReactElement> = {
  [ExpirationStatus.OK]: <OkIcon />,
  [ExpirationStatus.EXPIRING_SOON]: <WarningIcon />,
  [ExpirationStatus.EXPIRED]: <ExpiredIcon />,
};

// ============================================================================
// COMPONENT
// ============================================================================

export const ExamExpirationBadge: React.FC<ExamExpirationBadgeProps> = ({
  status,
  daysUntilExpiration,
  showDays = true,
  showIcon = true,
  showTooltip = true,
  size = 'small',
  ...props
}) => {
  const label = showDays && daysUntilExpiration !== undefined && daysUntilExpiration !== null
    ? formatDaysUntilExpiration(daysUntilExpiration)
    : ExpirationStatusLabels[status];

  const color = ExpirationStatusColors[status];
  const icon = showIcon ? statusIcons[status] : undefined;

  // Build tooltip content
  const getTooltipContent = (): string => {
    if (daysUntilExpiration === null || daysUntilExpiration === undefined) {
      return ExpirationStatusLabels[status];
    }

    switch (status) {
      case ExpirationStatus.EXPIRED:
        return `Vencido há ${Math.abs(daysUntilExpiration)} dia${Math.abs(daysUntilExpiration) !== 1 ? 's' : ''}`;
      case ExpirationStatus.EXPIRING_SOON:
        return `Vence em ${daysUntilExpiration} dia${daysUntilExpiration !== 1 ? 's' : ''}`;
      case ExpirationStatus.OK:
        return `Em dia - próximo exame em ${daysUntilExpiration} dia${daysUntilExpiration !== 1 ? 's' : ''}`;
      default:
        return ExpirationStatusLabels[status];
    }
  };

  const chip = (
    <Chip
      label={label}
      size={size}
      icon={icon}
      sx={{
        bgcolor: `${color}.100`,
        color: `${color}.800`,
        fontWeight: 600,
        '& .MuiChip-icon': {
          color: `${color}.700`,
        },
        ...props.sx,
      }}
      {...props}
    />
  );

  if (showTooltip) {
    return (
      <Tooltip title={getTooltipContent()} arrow>
        {chip}
      </Tooltip>
    );
  }

  return chip;
};

// ============================================================================
// SIMPLE STATUS INDICATOR
// ============================================================================

export interface ExpirationIndicatorProps {
  status: ExpirationStatus;
  size?: 'small' | 'medium' | 'large';
}

const indicatorSizes = {
  small: 8,
  medium: 12,
  large: 16,
};

export const ExpirationIndicator: React.FC<ExpirationIndicatorProps> = ({
  status,
  size = 'small',
}) => {
  const color = ExpirationStatusColors[status];
  const dimension = indicatorSizes[size];

  return (
    <Tooltip title={ExpirationStatusLabels[status]} arrow>
      <span
        style={{
          display: 'inline-block',
          width: dimension,
          height: dimension,
          borderRadius: '50%',
          backgroundColor: `var(--mui-palette-${color}-main)`,
        }}
      />
    </Tooltip>
  );
};

// ============================================================================
// EXPIRATION ICON ONLY
// ============================================================================

export interface ExpirationIconProps {
  status: ExpirationStatus;
  fontSize?: 'small' | 'medium' | 'large';
}

export const ExpirationIcon: React.FC<ExpirationIconProps> = ({
  status,
  fontSize = 'small',
}) => {
  const color = ExpirationStatusColors[status];
  const Icon = statusIcons[status].type;

  return (
    <Tooltip title={ExpirationStatusLabels[status]} arrow>
      <Icon fontSize={fontSize} sx={{ color: `${color}.main` }} />
    </Tooltip>
  );
};

export default ExamExpirationBadge;
